import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, LogIn, UserPlus, Gift, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { cn } from '../lib/utils';

/* ── Sub-components ── */

const LoginForm = ({ onSubmit, loading }) => {
  const [data, setData] = useState({ email: '', password: '' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(data); };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="login-email" className="text-slate-300">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500" size={18} />
          <Input id="login-email" type="email" placeholder="tu@email.com" value={data.email}
            onChange={(e) => setData({ ...data, email: e.target.value })}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            required disabled={loading} data-testid="login-email" />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="login-password" className="text-slate-300">Contrasena</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500" size={18} />
          <Input id="login-password" type="password" placeholder="........" value={data.password}
            onChange={(e) => setData({ ...data, password: e.target.value })}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            required disabled={loading} data-testid="login-password" />
        </div>
      </div>
      <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="login-submit">
        {loading ? (
          <span className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Ingresando...
          </span>
        ) : (
          <span className="flex items-center gap-2"><LogIn size={18} /> Iniciar Sesion</span>
        )}
      </Button>
    </form>
  );
};

const RegisterForm = ({ onSubmit, loading }) => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({ name: '', email: '', password: '', referral_code: '' });
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) setData(prev => ({ ...prev, referral_code: ref }));
  }, [searchParams]);
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(data); };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="register-name" className="text-slate-300">Nombre</Label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input id="register-name" type="text" placeholder="Tu nombre" value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            required disabled={loading} data-testid="register-name" />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="register-email" className="text-slate-300">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input id="register-email" type="email" placeholder="tu@email.com" value={data.email}
            onChange={(e) => setData({ ...data, email: e.target.value })}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            required disabled={loading} data-testid="register-email" />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="register-password" className="text-slate-300">Contrasena</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input id="register-password" type="password" placeholder="Minimo 6 caracteres" value={data.password}
            onChange={(e) => setData({ ...data, password: e.target.value })}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            minLength={6} required disabled={loading} data-testid="register-password" />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="register-referral" className="text-slate-300">Codigo de Referido (opcional)</Label>
        <div className="relative">
          <Gift className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input id="register-referral" type="text" placeholder="Ej: NMJUAN1234" value={data.referral_code}
            onChange={(e) => setData({ ...data, referral_code: e.target.value.toUpperCase() })}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 uppercase"
            disabled={loading} data-testid="register-referral" />
        </div>
        {data.referral_code && <p className="text-emerald-400 text-xs">Te registraras como referido!</p>}
      </div>
      <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="register-submit">
        {loading ? (
          <span className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Creando cuenta...
          </span>
        ) : (
          <span className="flex items-center gap-2"><UserPlus size={18} /> Crear Cuenta</span>
        )}
      </Button>
    </form>
  );
};

/* ── Main Modal ── */

const AuthModal = ({ isOpen, onClose, initialMode = 'login' }) => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const modalRef = useRef(null);
  const isClosingRef = useRef(false);
  const navPendingRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      isClosingRef.current = false;
      navPendingRef.current = false;
      setShouldRender(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setIsVisible(true)));
      document.body.style.overflow = 'hidden';
    } else {
      setIsVisible(false);
      const t = setTimeout(() => {
        if (!isClosingRef.current) setShouldRender(false);
        document.body.style.overflow = '';
      }, 200);
      return () => clearTimeout(t);
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    setIsVisible(false);
    setTimeout(() => {
      setShouldRender(false);
      onClose();
      isClosingRef.current = false;
      if (navPendingRef.current) {
        navPendingRef.current = false;
        queueMicrotask(() => navigate('/dashboard'));
      }
    }, 200);
  }, [onClose, navigate]);

  const handleAuthSuccess = useCallback(() => {
    navPendingRef.current = true;
    handleClose();
  }, [handleClose]);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape' && isOpen && !loading) handleClose(); };
    if (isOpen) document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, loading, handleClose]);

  const onBackdrop = useCallback((e) => {
    if (e.target === e.currentTarget && !loading) handleClose();
  }, [loading, handleClose]);

  const handleLogin = async (data) => {
    setLoading(true);
    try {
      await login(data.email, data.password);
      toast.success('Bienvenido de vuelta!');
      handleAuthSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar sesion');
    } finally { setLoading(false); }
  };

  const handleRegister = async (data) => {
    setLoading(true);
    try {
      await register(data.email, data.password, data.name, data.referral_code);
      toast.success('Cuenta creada exitosamente!');
      handleAuthSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear cuenta');
    } finally { setLoading(false); }
  };

  if (!shouldRender) return null;

  return (
    <div className={cn("fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-200",
      isVisible ? "opacity-100" : "opacity-0")} onClick={onBackdrop} role="dialog" aria-modal="true" data-testid="auth-modal-overlay">
      <div className="absolute inset-0 bg-black/80" aria-hidden="true" />
      <div ref={modalRef} className={cn(
        "relative z-10 w-full max-w-md mx-4 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl p-6 transition-all duration-200",
        isVisible ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-4"
      )} onClick={(e) => e.stopPropagation()} data-testid="auth-modal">
        <button onClick={handleClose} disabled={loading}
          className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity text-slate-400 hover:text-white disabled:opacity-30" aria-label="Cerrar">
          <X className="h-5 w-5" />
        </button>
        <div className="text-center mb-6">
          <h2 className="text-white text-xl font-semibold">Accede a NumeroMaestro</h2>
          <p className="text-slate-400 text-sm mt-1">Inicia sesion o crea una cuenta</p>
        </div>
        <Tabs defaultValue={initialMode} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-slate-800 mb-6">
            <TabsTrigger value="login" className="data-[state=active]:bg-green-600" data-testid="tab-login">Iniciar Sesion</TabsTrigger>
            <TabsTrigger value="register" className="data-[state=active]:bg-green-600" data-testid="tab-register">Registrarse</TabsTrigger>
          </TabsList>
          <TabsContent value="login"><LoginForm onSubmit={handleLogin} loading={loading} /></TabsContent>
          <TabsContent value="register"><RegisterForm onSubmit={handleRegister} loading={loading} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AuthModal;
